import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { businessApi } from '../services/api';
import { ROUTES, MESSAGES } from '../constants/Constants';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { getImageUrl } from '../utils/imageUtils';

const ViewBusiness = () => {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();

    useEffect(() => {
        fetchBusinesses();
    }, []);

    const fetchBusinesses = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await businessApi.fetchAll();
            if (Array.isArray(data)) {
                setBusinesses(data);
            } else {
                throw new Error(MESSAGES.ERROR.GENERAL);
            }
        } catch (err) {
            setError(err.message || MESSAGES.ERROR.GENERAL);
            toast.error(MESSAGES.ERROR.GENERAL);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this business?')) {
            try {
                await businessApi.delete(id);
                toast.success(MESSAGES.SUCCESS.BUSINESS_DELETED);
                fetchBusinesses(); // Refresh the list
            } catch (err) {
                toast.error(err.message || MESSAGES.ERROR.GENERAL);
            }
        }
    };

    const handleEdit = (id) => {
        navigate(ROUTES.BUSINESS.EDIT.replace(':id', id));
    };

    if (loading) {
        return (
            <Container className="mt-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">
                    {error}
                    <Button 
                        variant="link" 
                        className="p-0 ms-2"
                        onClick={fetchBusinesses}
                    >
                        Try again
                    </Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <h2 className="mb-4">Manage Businesses</h2>
            {businesses.length === 0 ? (
                <Alert variant="info">
                    No businesses found. 
                    {isAuthenticated && (
                        <Button 
                            variant="link" 
                            onClick={() => navigate(ROUTES.BUSINESS.ADD)}
                            className="p-0 ms-2"
                        >
                            Add one now
                        </Button>
                    )}
                </Alert>
            ) : (
                <Row>
                    {businesses.map(business => (
                        <Col key={business.id} md={6} lg={4} className="mb-4">
                            <Card>
                                <Card.Img 
                                    variant="top" 
                                    src={getImageUrl(business.profile_photo || business.photo) || '/images/placeholder-business.jpg'}
                                    alt={business.name}
                                    style={{ height: '200px', objectFit: 'cover' }}
                                />
                                <Card.Body>
                                    <Card.Title>{business.name}</Card.Title>
                                    <Card.Text>
                                        {business.description?.slice(0, 100)}
                                        {business.description?.length > 100 ? '...' : ''}
                                    </Card.Text>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                            {business.city} • {business.category}
                                        </small>
                                        <div>
                                            {/* Show Delete button only if the logged-in user is the owner */}
                                            {user && (business.ownerId === user.id || business.userId === user.id) && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(business.id)}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </Container>
    );
};

export default ViewBusiness;
